import { useEffect, useRef } from 'react';
import { useInitialRender } from 'src/shared/hooks/useInitialRender';
import { useAuth } from 'src/features/auth/contexts/AuthContext';
import { useOrg } from 'src/shared/contexts/OrgContext';

function get(key: 'lastActiveTimestamp' | 'loginTimestamp') {
  const data = localStorage.getItem(key);
  return data ? Number(data) : undefined;
}

function set(key: 'lastActiveTimestamp' | 'loginTimestamp', value: number) {
  localStorage.setItem(key, String(value));
}

export function useOrgTimeout() {
  const lastEvent = useRef(0);
  const { currentOrg, role } = useOrg();
  const { logout } = useAuth();

  useInitialRender(() => {
    const initial = get('loginTimestamp');
    if (!initial) {
      set('loginTimestamp', Date.now());
    }
  }, []);

  useEffect(() => {
    if (!currentOrg?.id) return;

    if (role === 'SUPPORT') {
      return;
    }

    const settings = currentOrg.settings;
    const timeout = settings?.generalTimeout;
    const inactivityTimeout = settings?.inactivityTimeout;

    const interval = setInterval(() => {
      set('lastActiveTimestamp', lastEvent.current);
      const lastActiveTimestamp = get('lastActiveTimestamp');
      const loginTimestamp = get('loginTimestamp');
      const now = Date.now();
      const timeSinceLastActive = (now - (lastActiveTimestamp || 0)) / 60000;
      const timeSinceLogin = (now - (loginTimestamp || 0)) / 60000;

      if (timeout && loginTimestamp && timeSinceLogin > timeout) {
        logout();
      }
      if (inactivityTimeout && lastActiveTimestamp && timeSinceLastActive > inactivityTimeout) {
        logout();
      }
    }, 1000);

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [currentOrg]);

  useEffect(() => {
    const controller = new AbortController();
    lastEvent.current = Date.now();
    window.addEventListener(
      'click',
      () => {
        lastEvent.current = Date.now();
      },
      { signal: controller.signal }
    );

    return () => {
      controller.abort();
    };
  }, []);
}
